import { useAppContext } from "@/providers/app-context";
import * as Sentry from "@sentry/react";
import { ScreenShareOffIcon } from "lucide-react";
import React, { ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export class ErrorBoundary extends React.Component<
  {
    children: ReactNode;
  },
  {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
  }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
  } {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
    Sentry.captureException(error, { extra: { componentStack: errorInfo?.componentStack } });
    console.error("Error caught by ErrorBoundary: ", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <GenericErrorComponent />;
    }
    return this.props.children;
  }
}

export const GenericErrorComponent = () => {
  const [searchParams] = useSearchParams();
  const error = searchParams.get("error");
  const next = searchParams.get("next");
  const navigate = useNavigate();
  const { setGlobalError } = useAppContext();

  React.useEffect(() => {
    if (error || next) {
      setGlobalError({
        error: error || "An error occurred",
        callback: () => navigate(next || "/"),
      });
    }
  }, []);

  return (
    <div className="flex h-full min-h-screen w-full flex-col items-center justify-center gap-y-4">
      <ScreenShareOffIcon className="h-32 w-32 text-muted-foreground" />
      <div className="flex flex-col gap-y-4 text-center">
        <h1 className="text-2xl">Something went wrong</h1>
        <p>
          We are sorry, but something went wrong. Please try refreshing the
          page.
        </p>
        <p>
          If the problem persists, please contact us at{" "}
          <a
            href="mailto:support@commonforge.com"
            className="cursor-pointer text-cf-foreground decoration-wavy underline-offset-2 hover:underline"
          >
            support@commonforge.com
          </a>
        </p>
      </div>
    </div>
  );
};
